/**
 * Consent banner - first-visit, granular 3-type (essential/analytics/personalization)
 *
 * Per F-FE-LANDINGS.md Sec.4.2 + AC-6 + AC-15. Default = essential-only on
 * ESC dismiss. Mobile bottom-sticky compact (per OQ-MD14-03 RESOLVED).
 *
 * BUG-2026-05-05-02: banner copy + button labels render via useLang();
 * payload `lang` field reads context (was hardcoded 'vi').
 */

import { useEffect, useState } from 'react';
import { essentialOnlyChoice } from '@core/landings-helpers';
import type { ConsentChoice } from '@core/landings-helpers';
import { useLang } from '../use-lang';

const CONSENT_KEY = 'asklaw_consent';

function readConsent(): string | null {
  try {
    const ls = window.localStorage.getItem(CONSENT_KEY);
    if (ls) return ls;
  } catch {
    // localStorage may throw in private mode - fall through to cookie
  }
  const cookie = document.cookie
    .split(';')
    .map((p) => p.trim())
    .find((p) => p.startsWith(`${CONSENT_KEY}=`))
    ?.split('=')[1];
  return cookie ?? null;
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice>(essentialOnlyChoice());
  const { lang } = useLang();

  const t = {
    region: lang === 'vi' ? 'Tuỳ chọn cookie' : 'Cookie consent',
    close: lang === 'vi'
      ? 'Đóng (chỉ chấp nhận cookie thiết yếu)'
      : 'Close (essential cookies only)',
    body: lang === 'vi'
      ? 'Asklaw dùng cookie thiết yếu (đăng nhập, ngôn ngữ, tiền tệ). Tùy chọn analytics để cải thiện UX. Cookie cá nhân hoá chỉ dùng nếu bạn đăng ký Pro (sau).'
      : 'Asklaw uses essential cookies (auth, language, currency). Analytics is optional, helps us improve UX. Personalization cookies are only used if you upgrade to Pro (later).',
    essential: lang === 'vi' ? 'Thiết yếu (bắt buộc)' : 'Essential (required)',
    analytics: lang === 'vi' ? 'Phân tích' : 'Analytics',
    personalization: lang === 'vi'
      ? 'Cá nhân hoá (Pro)'
      : 'Personalization (Pro only)',
    save: lang === 'vi' ? 'Lưu lựa chọn' : 'Save choice',
    essentialOnly: lang === 'vi' ? 'Chỉ thiết yếu' : 'Essential only',
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = readConsent();
    if (existing) return;
    const t = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && visible) submit(essentialOnlyChoice());
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  async function submit(c: ConsentChoice) {
    const payload = JSON.stringify({
      essential: true,
      analytics: c.analytics,
      personalization: c.personalization,
      lang,
    });
    try {
      window.localStorage.setItem(CONSENT_KEY, payload);
    } catch {
      // private mode - fall through to cookie
    }
    document.cookie = `${CONSENT_KEY}=${encodeURIComponent(payload)}; Path=/; Max-Age=${
      60 * 60 * 24 * 365
    }; SameSite=Lax`;
    setVisible(false);
    try {
      await fetch('/api/v1/public/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });
    } catch {
      // server logging best-effort - local persistence already done above
    }
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label={t.region}
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 460,
        marginLeft: 'auto',
        background: 'var(--color-bg, #fff)',
        border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
        borderRadius: 12,
        padding: 16,
        zIndex: 50,
        boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
      }}
    >
      <button
        type="button"
        aria-label={t.close}
        onClick={() => submit(essentialOnlyChoice())}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          color: 'var(--color-muted, #6b6b6b)',
        }}
      >
        x
      </button>
      <p style={{ marginTop: 0, marginRight: 24, fontSize: 13, lineHeight: 1.5 }}>{t.body}</p>
      <fieldset
        style={{
          border: 0,
          padding: 0,
          margin: '8px 0',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          fontSize: 13,
        }}
      >
        <label>
          <input type="checkbox" checked disabled /> {t.essential}
        </label>
        <label>
          <input
            type="checkbox"
            checked={choice.analytics}
            onChange={(e) => setChoice({ ...choice, analytics: e.target.checked })}
          />{' '}
          {t.analytics}
        </label>
        <label style={{ opacity: 0.5 }}>
          <input type="checkbox" disabled /> {t.personalization}
        </label>
      </fieldset>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="cta-primary" onClick={() => submit(choice)} style={{ fontSize: 14 }}>
          {t.save}
        </button>
        <button
          className="cta-secondary"
          onClick={() => submit(essentialOnlyChoice())}
          style={{ fontSize: 14 }}
        >
          {t.essentialOnly}
        </button>
      </div>
    </div>
  );
}
